import {
  ReactNode,
  MutableRefObject,
  ElementType,
  ComponentPropsWithoutRef,
  useMemo,
  forwardRef,
  useRef,
  useEffect,
  HTMLAttributes,
} from 'react'
import { styled, darken, useForkRef, SxProps } from '@mui/material'
import SimpleBarCore from 'simplebar-core'
import type { SimpleBarOptions } from 'simplebar-core'
import 'simplebar/dist/simplebar.css'
import {
  CUSTOM_SCROLL_CLASS,
  HIDE_SCROLLBAR_CLASS,
} from '$setup/style/constants'
import { nativeScrollbarWidth } from '$func/environmentInfo'

type RenderFunc = (props: {
  scrollableNodeRef: MutableRefObject<HTMLElement | undefined>
  scrollableNodeProps: {
    className: string
    ref: MutableRefObject<HTMLElement | undefined>
  }
  contentNodeRef: MutableRefObject<HTMLElement | undefined>
  contentNodeProps: {
    className: string
    ref: MutableRefObject<HTMLElement | undefined>
  }
  scrollableNodeClass: string
}) => ReactNode

export interface Props
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    SimpleBarOptions {
  children?: ReactNode | RenderFunc
  scrollableNodeProps?: ComponentPropsWithoutRef<'div'>
  scrollableNodeChildrenSlot?: ReactNode
  scrollableElement?: ElementType
  useNativeOverlayScrollbars?: boolean
  contentNodeSx?: SxProps
}

export const Scroller = forwardRef<HTMLElement | undefined, Props>(
  function ScrollerComponent(
    {
      scrollableElement = 'div',
      children,
      scrollableNodeProps = {},
      scrollableNodeChildrenSlot,
      useNativeOverlayScrollbars = true,
      style: rootStyle,
      contentNodeSx,
      ...otherProps
    },
    ref,
  ) {
    const Tag = scrollableElement
    const elRef = useRef<HTMLDivElement>(null)
    const scrollableNodeRef = useRef<HTMLElement>()
    const handleScrollableNodeRef = useForkRef(scrollableNodeRef, ref)
    const contentNodeRef = useRef<HTMLElement>()

    const shouldRenderNativeScrollbars = useMemo(() => {
      return useNativeOverlayScrollbars && nativeScrollbarWidth === 0
    }, [useNativeOverlayScrollbars])

    const scrollableClass = useMemo(() => {
      if (shouldRenderNativeScrollbars) return CUSTOM_SCROLL_CLASS
      return HIDE_SCROLLBAR_CLASS
    }, [shouldRenderNativeScrollbars])

    const { options, rest } = useMemo(() => {
      const resultOptions: Partial<SimpleBarOptions> = {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resultRest: any = {}

      Object.keys(otherProps).forEach(key => {
        if (
          Object.prototype.hasOwnProperty.call(
            SimpleBarCore.defaultOptions,
            key,
          )
        ) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          resultOptions[key] = otherProps[key as keyof SimpleBarOptions]
        } else {
          resultRest[key] = otherProps[key as keyof SimpleBarOptions]
        }
      })

      return { options: resultOptions, rest: resultRest }
    }, [otherProps])

    const classNames = {
      ...SimpleBarCore.defaultOptions.classNames,
      ...options.classNames,
    } as Required<(typeof SimpleBarCore.defaultOptions)['classNames']>

    const scrollableNodeFullProps = {
      ...scrollableNodeProps,
      className: `${classNames.contentWrapper} ${scrollableClass} ${
        scrollableNodeProps.className ? ` ${scrollableNodeProps.className}` : ''
      }`,
      tabIndex: options.tabIndex || SimpleBarCore.defaultOptions.tabIndex,
      role: 'region',
      'aria-label': options.ariaLabel || SimpleBarCore.defaultOptions.ariaLabel,
    }

    useEffect(() => {
      let instance: SimpleBarCore | null

      if (elRef.current && !shouldRenderNativeScrollbars) {
        instance = new SimpleBarCore(elRef.current, {
          ...options,
          ...(scrollableNodeRef.current && {
            scrollableNode: scrollableNodeRef.current,
          }),
          ...(contentNodeRef.current && {
            contentNode: contentNodeRef.current,
          }),
        })
      }

      return () => {
        instance?.unMount()
        instance = null
      }
    }, [shouldRenderNativeScrollbars, options])

    if (shouldRenderNativeScrollbars)
      return typeof children === 'function' ? (
        <Root ref={elRef} style={{ ...rootStyle }} {...rest}>
          {children({
            scrollableNodeRef,
            scrollableNodeProps: {
              ...scrollableNodeFullProps,
              ref: scrollableNodeRef,
            },
            scrollableNodeClass: scrollableClass,
            contentNodeRef,
            contentNodeProps: {
              className: classNames.contentEl,
              ref: contentNodeRef,
            },
          })}
        </Root>
      ) : (
        <Root ref={elRef} style={rootStyle} {...rest}>
          <Tag ref={handleScrollableNodeRef} {...scrollableNodeFullProps}>
            {scrollableNodeChildrenSlot}
            {children}
          </Tag>
        </Root>
      )

    return (
      <Root data-simplebar="init" ref={elRef} {...rest} style={rootStyle}>
        <div className={classNames.wrapper}>
          <div className={classNames.heightAutoObserverWrapperEl}>
            <div className={classNames.heightAutoObserverEl} />
          </div>
          <div className={classNames.mask}>
            <div className={classNames.offset}>
              {typeof children === 'function' ? (
                children({
                  scrollableNodeRef,
                  scrollableNodeProps: {
                    ...scrollableNodeFullProps,
                    ref: scrollableNodeRef,
                  },
                  scrollableNodeClass: scrollableClass,
                  contentNodeRef,
                  contentNodeProps: {
                    className: classNames.contentEl,
                    ref: contentNodeRef,
                  },
                })
              ) : (
                <Tag {...scrollableNodeFullProps} ref={handleScrollableNodeRef}>
                  {scrollableNodeChildrenSlot}
                  <Content className={classNames.contentEl} sx={contentNodeSx}>
                    {children}
                  </Content>
                </Tag>
              )}
            </div>
          </div>
          <div className={classNames.placeholder} />
        </div>
        <div className={`${classNames.track} simplebar-horizontal`}>
          <div className={classNames.scrollbar} />
        </div>
        <div className={`${classNames.track} simplebar-vertical`}>
          <div className={classNames.scrollbar} />
        </div>
      </Root>
    )
  },
)

const Root = styled('div')`
  max-height: 100%;
  &.flex-end {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    .simplebar-offset {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }
  }

  .${CUSTOM_SCROLL_CLASS},.${HIDE_SCROLLBAR_CLASS} {
    direction: rtl !important;
  }

  .simplebar-track.simplebar-vertical {
    left: 0;
    right: auto;
  }

  .simplebar-scrollbar {
    &:before {
      background: ${({ theme }) => darken(theme.palette.neutral300, 0.1)};
      transition:
        opacity 0.2s 0.5s linear,
        color 0.2s ease;
    }
  }

  .simplebar-scrollbar.simplebar-visible:before {
    opacity: 1;
  }
`

const Content = styled('div')``
